import { TServiceTicket, TExpoServiceSummary, TExpoDay, TPosition } from "~/types";
import { baseService } from "../base";
import { PaginationResponse } from "../type";
import {
  CreateServiceTicketDto,
  GetServiceTicketParams,
  ImportServiceTicketsResponse,
  UpdateServiceTicketDto,
  CompleteServiceTicketDto,
} from "./service-ticket.types";
import {
  TSaveExpoDaysDto,
  TGenerateExpoDaysDto,
  TCopyFromDayOneDto,
  TApplyPositionToAllDaysDto,
} from "~/types/expo-service";

export const serviceTicketService = {
  createServiceTicket: async (payload: CreateServiceTicketDto) => {
    // Convert addOnServices to array of IDs if needed
    const addOnServices = payload.addOnServices?.map((item) =>
      typeof item === "string" ? item : item.id
    );

    const requestPayload = {
      ...payload,
      addOnServices,
    };

    console.log('Creating service ticket with payload:', JSON.stringify(requestPayload, null, 2));

    try {
      const { data } = await baseService.post("/service-tickets", requestPayload);
      console.log('Service ticket created successfully:', data);
      return data;
    } catch (error: any) {
      console.error('Error creating service ticket:', error);
      console.error('Error response data:', JSON.stringify(error.response?.data, null, 2));
      console.error('Error status:', error.response?.status);
      console.error('Error message from server:', error.response?.data?.message);
      console.error('Full error response:', error.response);
      throw error;
    }
  },
  updateServiceTicket: async ({ id, ...payload }: UpdateServiceTicketDto) => {
    // Convert addOnServices to array of IDs if needed
    const addOnServices = payload.addOnServices?.map((item) =>
      typeof item === "string" ? item : item.id
    );

    const requestPayload = {
      ...payload,
      addOnServices,
    };

    console.log('Updating service ticket with payload:', JSON.stringify(requestPayload, null, 2));

    try {
      const { data } = await baseService.patch(`/service-tickets/${id}`, requestPayload);
      console.log('Service ticket updated successfully:', data);
      return data;
    } catch (error: any) {
      console.error('Error updating service ticket:', error);
      console.error('Error response data:', JSON.stringify(error.response?.data, null, 2));
      console.error('Error status:', error.response?.status);
      console.error('Error message from server:', error.response?.data?.message);
      console.error('Full error response:', error.response);
      throw error;
    }
  },
  getServiceTickets: async (params?: GetServiceTicketParams) => {
    const { data } = await baseService.get<PaginationResponse<TServiceTicket>>(
      "/service-tickets",
      {
        params: params ?? {
          page: 1,
          pageSize: 50,
        },
      }
    );
    return data;
  },
  getServiceTicketsForDashboard: async (params?: GetServiceTicketParams) => {
    const { data } = await baseService.get<PaginationResponse<TServiceTicket>>(
      "/service-tickets/dashboard",
      {
        params: params ?? {
          page: 1,
          pageSize: 200,
        },
      }
    );
    return data;
  },
  getServiceTicket: async (id: string) => {
    const { data } = await baseService.get<TServiceTicket>(
      `/service-tickets/${id}`
    );
    return data;
  },
  getMyServiceTickets: async (params?: GetServiceTicketParams) => {
    const { data } = await baseService.get<PaginationResponse<TServiceTicket>>(
      "/service-tickets/my-service",
      {
        params,
      }
    );
    return data;
  },
  // completeServiceTicket: async (id: string) => {
  //   const { data } = await baseService.patch(`/service-tickets/${id}/complete`);
  //   return data;
  // },
  completeServiceTicket: async (payload: CompleteServiceTicketDto) => {
    const { data } = await baseService.patch(
      `/service-tickets/${payload.id}/complete`,
      payload
    );
    return data;
  },
  deleteServiceTicket: async (id: string) => {
    const { data } = await baseService.delete(`/service-tickets/${id}`);
    return data;
  },
  importServiceTickets: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await baseService.post<ImportServiceTicketsResponse>(
      "/service-tickets/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },
  getUpcomingServiceTicketsByInventoryId: async (inventoryId: string, params?: GetServiceTicketParams) => {
    const { data } = await baseService.get<PaginationResponse<TServiceTicket>>(
      `/service-tickets/by-inventory/${inventoryId}`,
      {
        params: params ?? {
          page: 1,
          pageSize: 10,
        },
      }
    );
    return data;
  },
  getHistoryServiceTicketsByInventoryId: async (inventoryId: string, params?: GetServiceTicketParams) => {
    const { data } = await baseService.get<PaginationResponse<TServiceTicket>>(
      `/service-tickets/history-by-inventory/${inventoryId}`,
      {
        params: params ?? {
          page: 1,
          pageSize: 10,
        },
      }
    );
    return data;
  },
  getServiceTicketInventories: async (id: string) => {
    const { data } = await baseService.get<TServiceTicket>(
      `/service-tickets/${id}/inventories`
    );
    return data;
  },
  bulkManageInventory: async (params: {
    inventoryIds: string[];
    serviceTicketIds: string[];
    action: 'add' | 'remove';
  }) => {
    const { data } = await baseService.post('/service-tickets/bulk-inventory', params);
    return data;
  },
  calculateEstimate: async (input: {
    inventoryIds?: string[];
    templateItems?: { templateId: string; quantity: number }[];
    addOnServiceIds?: string[];
  }) => {
    const { data } = await baseService.post(
      `/service-tickets/calculate-estimate`,
      input
    );
    return data;
  },
  getTodayMetrics: async (date?: string, startDate?: string, endDate?: string) => {
    const params: any = {};
    if (date) params.date = date;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const { data } = await baseService.get(
      "/service-tickets/today-metrics",
      { params }
    );
    return data;
  },
  getIndividualDailyMetrics: async (startDate: string, endDate: string) => {
    const params = { startDate, endDate };
    const { data } = await baseService.get(
      "/daily-metrics/days",
      { params }
    );
    return data;
  },
  updateDailyMetricsOverrides: async (date: string, overrides: {
    staffHoursOverride?: number | null;
    vesselHoursOverride?: number | null;
    materialsCostOverride?: number | null;
  }) => {
    // Filter out undefined values but keep null values
    const payload: any = {};
    if (overrides.staffHoursOverride !== undefined) {
      payload.staffHoursOverride = overrides.staffHoursOverride;
    }
    if (overrides.vesselHoursOverride !== undefined) {
      payload.vesselHoursOverride = overrides.vesselHoursOverride;
    }
    if (overrides.materialsCostOverride !== undefined) {
      payload.materialsCostOverride = overrides.materialsCostOverride;
    }
    
    console.log('PUT /daily-metrics/' + date + '/overrides', payload);
    
    const { data } = await baseService.put(
      `/daily-metrics/${date}/overrides`,
      payload
    );
    return data;
  },
  syncDailyMetrics: async (date?: string, startDate?: string, endDate?: string) => {
    const params: any = {};
    if (date) params.date = date;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const { data } = await baseService.post(
      "/daily-metrics/sync",
      {},
      { params }
    );
    return data;
  },

  // ==========================================
  // EXPO MODE - Multi-day Service Scheduling
  // ==========================================

  // Get expo days for a service ticket
  getExpoDays: async (serviceTicketId: string): Promise<TExpoServiceSummary> => {
    const { data } = await baseService.get<TExpoServiceSummary>(
      `/service-tickets/${serviceTicketId}/expo-days`
    );
    return data;
  },

  // Save expo days (create/update/delete)
  saveExpoDays: async (serviceTicketId: string, payload: TSaveExpoDaysDto): Promise<TExpoServiceSummary> => {
    const { data } = await baseService.post<TExpoServiceSummary>(
      `/service-tickets/${serviceTicketId}/expo-days`,
      payload
    );
    return data;
  },

  // Generate days preview from date range (does not save)
  generateExpoDaysPreview: async (serviceTicketId: string, payload: TGenerateExpoDaysDto): Promise<TExpoDay[]> => {
    const { data } = await baseService.post<TExpoDay[]>(
      `/service-tickets/${serviceTicketId}/expo-days/generate-preview`,
      payload
    );
    return data;
  },

  // Copy positions from Day 1 to other days
  copyFromDayOne: async (serviceTicketId: string, payload: TCopyFromDayOneDto): Promise<TExpoServiceSummary> => {
    const { data } = await baseService.post<TExpoServiceSummary>(
      `/service-tickets/${serviceTicketId}/expo-days/copy-from-day-one`,
      payload
    );
    return data;
  },

  // Apply a position to all days
  applyPositionToAllDays: async (serviceTicketId: string, payload: TApplyPositionToAllDaysDto): Promise<TExpoServiceSummary> => {
    const { data } = await baseService.post<TExpoServiceSummary>(
      `/service-tickets/${serviceTicketId}/expo-days/apply-position-to-all`,
      payload
    );
    return data;
  },

  // Delete all expo days for a service ticket
  deleteExpoDays: async (serviceTicketId: string): Promise<void> => {
    await baseService.delete(`/service-tickets/${serviceTicketId}/expo-days`);
  },

  // Get available positions for dropdown
  getAvailablePositions: async (): Promise<TPosition[]> => {
    const { data } = await baseService.get<TPosition[]>("/service-tickets/expo/positions");
    return data;
  },

  // ==========================================
  // BOOKING-LEVEL EXPO MODE
  // ==========================================

  // Save expo days at booking level (creates/updates service tickets as expo days)
  saveBookingExpoDays: async (bookingId: string, payload: TSaveExpoDaysDto): Promise<TExpoServiceSummary> => {
    const { data } = await baseService.post<TExpoServiceSummary>(
      `/jobtickets/${bookingId}/expo-days`,
      payload
    );
    return data;
  },
};
